import { useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

/**
 * CollapsibleSection — wraps any admin card so it starts COLLAPSED
 * (just a clickable header) and expands on click.
 *
 * Keeps the admin page scrollable without scrolling forever. Use for
 * any panel that isn't critical day-1 information.
 *
 * Props:
 *  - title:      heading text (required)
 *  - subtitle:   small grey explainer under the title (optional)
 *  - icon:       Phosphor icon component (optional)
 *  - tone:       "default" | "primary" — primary draws a light orange ring
 *  - defaultOpen: start expanded (default: false)
 *  - count:      small number badge next to the title (optional)
 *  - children:   the panel content, rendered only when expanded
 *
 * Lazy-mount intentional: children aren't rendered until first open,
 * so cards that fetch on mount (e.g. analytics) don't fire until the
 * admin actively wants the data.
 */
export default function CollapsibleSection({
  title,
  subtitle,
  icon: Icon,
  tone = "default",
  defaultOpen = false,
  count,
  children,
  testId,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [mountedOnce, setMountedOnce] = useState(defaultOpen);
  const toggle = () => {
    if (!open && !mountedOnce) setMountedOnce(true);
    setOpen((v) => !v);
  };
  return (
    <section
      className={
        "rounded-2xl border bg-card " +
        (tone === "primary"
          ? "border-primary/40 ring-1 ring-primary/10"
          : "border-border")
      }
      data-testid={testId || "collapsible-section"}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-muted/40 transition-colors rounded-2xl"
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : "collapsible-section-toggle"}
      >
        {open ? (
          <CaretDown weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
        ) : (
          <CaretRight weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
        )}
        {Icon && <Icon weight="duotone" className="h-5 w-5 text-primary flex-none" />}
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-base tracking-tight flex items-center gap-2">
            {title}
            {typeof count === "number" && (
              <span className="text-[10px] uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground">
                {count}
              </span>
            )}
          </div>
          {subtitle && (
            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{subtitle}</div>
          )}
        </div>
      </button>
      {mountedOnce && (
        <div className={open ? "px-5 pb-5 pt-1" : "hidden"} aria-hidden={!open}>
          {children}
        </div>
      )}
    </section>
  );
}
